<template>
  <StepContainer
    v-loading="isLoading"
    class="final-check-step"
    :title="$t('APPS.DRAFT.STEPS.FINAL_CHECK')"
    :description="$t('APPS.DRAFT.STEPS.FINAL_CHECK.DESCRIPTION')"
  >
    <DraftAppIcon />

    <KeyValueDataView
      :title="$t('APPS.DRAFT.STEPS.BASIC_INFO')"
      :items="basicInfo"
    />

    <KeyValueDataView
      :title="$t('APPS.DRAFT.STEPS.SUPPORT_INFO')"
      :items="supportInfo"
    />

    <NewAppPackagesTable
      v-if="draftApp.packages.length"
      :packages="draftApp.packages"
      :features-and-libraries="draftApp.featuresAndLibraries"
      readonly
    />

    <header class="final-check-step__heading">
      {{ $t('APP.DRAFT.STEPS.FINAL_CHECK.PUBLISHING_TYPE') }}
      <Hint :message="$t('HINTS.APPS.RELEASES.AUTO_PUBLISH')" />
    </header>

    <ElRadioGroup v-model="autoPublish" class="final-check-step__auto-publish">
      <ElRadio :label="false">
        {{ $t('APPS.DRAFT.STEPS.FINAL_CHECK.MANUAL_PUBLISH') }}
      </ElRadio>
      <ElRadio :label="true">
        {{ $t('APPS.DRAFT.STEPS.FINAL_CHECK.AUTO_PUBLISH_AFTER_APPROVAL') }}
      </ElRadio>
    </ElRadioGroup>

    <StepControllers slot="actions" @change-step="handleStepChange" />

    <PublishRequestModal
      v-model="showPublishRequestModal"
      :note.sync="developerNote"
      :file.sync="developerFile"
      :release="draftApp"
      :loading="isLoading"
      :app="draftApp"
      @confirm="commitNewApp"
    />
  </StepContainer>
</template>

<script>
import StepContainer from '../common/StepContainer';
import { mapActions, mapGetters } from 'vuex';
import { CHANGE_DRAFT_APP_STATE, DRAFT_APP } from '@/store/types';
import KeyValueDataView from './KeyValueDataView';
import DraftAppIcon from './DraftAppIcon';
import NewAppPackagesTable from '../common/NewAppPackagesTable';
import Hint from '@@/common/Hint';
import getDraftAppPrevStep from '../common/getDraftAppPrevStep';
import PublishRulesModal from '../../modal/PublishRulesModal';
import PublishRequestModal from '@@/app/AppReleases/common/PublishRequestModal';
import StepControllers from '../common/StepControllers';
import { APP_CREATION_STEPS } from '../common/constants';
import { DraftApps } from '../../../../api/api';

export default {
  name: 'FinalCheckStep',
  components: {
    StepContainer,
    KeyValueDataView,
    DraftAppIcon,
    NewAppPackagesTable,
    Hint,
    PublishRequestModal,
    StepControllers,
  },
  inject: ['stepperController'],
  data() {
    return {
      isLoading: false,
      autoPublish: false,
      developerNote: '',
      developerFile: null,
      showPublishRequestModal: false,
    };
  },
  computed: {
    ...mapGetters({ draftApp: DRAFT_APP }),

    prevStep() {
      return getDraftAppPrevStep(this.draftApp?.currentState);
    },

    basicInfo() {
      const { draftApp } = this;
      const inAppPurchaseStatus = draftApp.haveIap
        ? ' - ' + this.$t('APPS.DRAFT.STEPS.BASIC_INFO.HAVE_IAP')
        : '';
      const pricingMethodStatus =
        draftApp.pricingMethod === 'F'
          ? this.$t('APPS.DRAFT.STEPS.BASIC_INFO.PRICING_METHOD.FREE')
          : this.$t(
              'APPS.DRAFT.STEPS.BASIC_INFO.PRICING_METHOD.PAY_BEFORE_DOWNLOAD',
            );

      return [
        {
          key: 'nameFa',
          name: this.$t('APPS.DRAFT.STEPS.APP_INFO.APP_NAME_FA'),
          value: draftApp.nameFa,
        },
        {
          key: 'nameEn',
          name: this.$t('APPS.DRAFT.STEPS.APP_INFO.APP_NAME_EN'),
          value: draftApp.nameEn || '-',
        },
        {
          key: 'appType',
          name: this.$t('APPS.DRAFT.STEPS.BASIC_INFO.APP_TYPE'),
          value:
            draftApp.appType === 'A'
              ? this.$t('APPS.DRAFT.STEPS.BASIC_INFO.APP_TYPE.APPLICATION')
              : this.$t('APPS.DRAFT.STEPS.BASIC_INFO.APP_TYPE.GAME'),
        },
        {
          key: 'category',
          name: this.$t('APPS.DRAFT.STEPS.BASIC_INFO.APP_CATEGORY'),
          value: draftApp.category.name,
        },
        {
          key: 'pricingMethod',
          name: this.$t('APPS.DRAFT.STEPS.BASIC_INFO.PRICING_METHOD'),
          value: pricingMethodStatus + inAppPurchaseStatus,
        },
        {
          key: 'price',
          name: this.$t('APPS.DRAFT.STEPS.BASIC_INFO.PRICE'),
          value: `${draftApp.price} ${this.$t('RIALS')}`,
        },
      ];
    },

    supportInfo() {
      const { draftApp } = this;

      return [
        {
          key: 'phone',
          name: this.$t('APPS.DRAFT.STEPS.SUPPORT_INFO.PHONE'),
          value: draftApp.phone || '-',
        },
        {
          key: 'email',
          name: this.$t('APPS.DRAFT.STEPS.SUPPORT_INFO.EMAIL'),
          value: draftApp.email,
        },
        {
          key: 'homepageFa',
          name: this.$t('APPS.DRAFT.STEPS.SUPPORT_INFO.PERSIAN_WEBSITE'),
          value: draftApp.homepageFa || '-',
        },
        {
          key: 'homepageEn',
          name: this.$t('APPS.DRAFT.STEPS.SUPPORT_INFO.ENGLISH_WEBSITE'),
          value: draftApp.homepageEn || '-',
        },
      ];
    },
  },
  created() {
    this.stepperController.onStepClick(this.goToStep);
  },
  beforeDestroy() {
    this.stepperController.removeOnStepClick(this.goToStep);
  },
  methods: {
    ...mapActions({ checkAndChangeStep: CHANGE_DRAFT_APP_STATE }),

    handleStepChange(target) {
      if (target === APP_CREATION_STEPS.COMMITTED) {
        this.showPublishRulesModal().then(confirmed => {
          if (confirmed) {
            this.showPublishRequestModal = true;
          }
        });
      } else {
        this.goToStep(target);
      }
    },

    commitNewApp() {
      this.isLoading = true;
      const { currentState, id } = this.draftApp;

      const data = {
        autoPublish: this.autoPublish,
        developerNote: this.developerNote,
        developerFile: this.developerFile,
      };

      return DraftApps.publish({ id, data, currentState })
        .then(({ message }) => {
          this.$message.success(message);
          this.$router
            .push({
              name: 'apps-view',
              params: { packageName: this.draftApp.packageName },
            })
            .catch(() => {});
        })
        .catch(({ message }) => {
          this.$message.error(message);
          this.isLoading = false;
        });
    },

    showPublishRulesModal() {
      const categoryId = this.draftApp?.category?.id;
      return this.$modal.open({
        title: this.$t('APP.RELEASES.PUBLISH_RULES'),
        component: PublishRulesModal,
        props: {
          categoryId,
          key: categoryId,
        },
        width: '35%',
        lockScroll: true,
      });
    },

    goToStep(step) {
      this.isLoading = true;

      this.checkAndChangeStep({ to: step })
        .catch(({ message, errors = [] }) => {
          this.$message.error(message);
          this.errors = errors;
        })
        .finally(() => (this.isLoading = false));
    },
  },
};
</script>

<style lang="scss">
.final-check-step {
  &__heading {
    font-size: 110%;
    font-weight: 500;
    margin-bottom: $global-margin/2;
    margin-top: 2 * $global-margin;
  }

  &__auto-publish {
    margin-bottom: $global-margin * 2;
    margin-top: $global-margin;
  }
}
</style>
